import { Alert, Checkbox, Radio, Select, Typography } from "antd"
import type { BaseOptionType } from "antd/lib/select"
import React, { useEffect, useMemo, useState } from "react"

import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import {
  selectAccessibleBranches,
  selectActiveBranchId
} from "../../../Branches/redux/branches.slice.ts"
import type {
  BranchAccessMode,
  BranchAccessPayload,
  TeamMember
} from "../../types/types.ts"

const { Text } = Typography

// Never grantable through branch access — the HQ/ownership tier. Mirrors
// apps.user.permission_registry.ADMIN_LABEL_CODES on ezbuild_back exactly;
// apps.user.services.branch_access_service refuses these server-side too,
// so this is a UX convenience, not the actual security boundary.
const RESERVED_ROLE_CODES = new Set(["gcc", "gcc_admin"])

const MODE_SPECIFIC: BranchAccessMode = "specific"
const MODE_ALL: BranchAccessMode = "all"

type RoleOption = BaseOptionType & { code: string }

// Editing an existing member has to open on THEIR actual branches and roles,
// not the actor's own — the only place a blank "Select at least one branch"
// prompt is correct is a brand-new invite, seeded from the actor's active
// branch instead. Derived once, outside the component, so it stays a pure
// function of the member being edited (or its absence, for an invite).
function deriveInitialAccessState(
  member: Pick<TeamMember, "branches" | "company_wide_access"> | undefined,
  activeBranchId: string | null | undefined
) {
  if (member?.company_wide_access) {
    return {
      mode: MODE_ALL,
      teamIds: [] as string[],
      defaultRoleCode: member.company_wide_access.role_code as string | undefined,
      defaultTeamId: undefined as string | undefined,
      roleOverrides: {} as Record<string, string>,
      showOverrides: false,
      // The grant already exists — showing the checkbox unticked would
      // misrepresent reality and would also block saving an unrelated field
      // (name, status) with "select at least one branch and a role".
      confirmAllBranches: true
    }
  }

  if (member?.branches?.length) {
    const teamIds = member.branches.map((b) => b.team_id)
    const defaultBranch =
      member.branches.find((b) => b.is_default_branch) ?? member.branches[0]
    const defaultRoleCode = defaultBranch.role_code ?? undefined
    const roleOverrides: Record<string, string> = {}
    for (const branch of member.branches) {
      if (branch.role_code && branch.role_code !== defaultRoleCode) {
        roleOverrides[branch.team_id] = branch.role_code
      }
    }

    return {
      mode: MODE_SPECIFIC,
      teamIds,
      defaultRoleCode,
      defaultTeamId: defaultBranch.team_id,
      roleOverrides,
      showOverrides: Object.keys(roleOverrides).length > 0,
      confirmAllBranches: false
    }
  }

  return {
    mode: MODE_SPECIFIC,
    teamIds: activeBranchId ? [activeBranchId] : ([] as string[]),
    defaultRoleCode: undefined as string | undefined,
    defaultTeamId: activeBranchId ?? undefined,
    roleOverrides: {} as Record<string, string>,
    showOverrides: false,
    confirmAllBranches: false
  }
}

interface TeamMemberBranchAccessSectionProps {
  roleOptions: RoleOption[]
  disabled?: boolean
  // The member currently being edited — absent for a new invite.
  initialMember?: Pick<TeamMember, "branches" | "company_wide_access">
  onChange: (payload: BranchAccessPayload | undefined) => void
}

export const TeamMemberBranchAccessSection = ({
  roleOptions,
  disabled,
  initialMember,
  onChange
}: TeamMemberBranchAccessSectionProps) => {
  const accessibleBranches = useAppSelector(selectAccessibleBranches)
  const activeBranchId = useAppSelector(selectActiveBranchId)

  // `value` is the role CODE (stable across every branch's own copy of the
  // label), never the label's per-team database id — a Select's onChange
  // must yield something that means the same thing on every branch it's
  // applied to.
  const grantableRoles = useMemo(
    () =>
      roleOptions
        .filter((option) => !RESERVED_ROLE_CODES.has(option.code))
        .map((option) => ({ value: option.code, label: option.label })),
    [roleOptions]
  )

  // Computed fresh each render, but only ever read by the useState calls
  // below on mount — cheap, and avoids a stale closure over the member this
  // section originally opened for.
  const initial = deriveInitialAccessState(initialMember, activeBranchId)

  const [mode, setMode] = useState<BranchAccessMode>(initial.mode)
  const [teamIds, setTeamIds] = useState<string[]>(initial.teamIds)
  const [defaultRoleCode, setDefaultRoleCode] = useState<string | undefined>(initial.defaultRoleCode)
  const [showOverrides, setShowOverrides] = useState(initial.showOverrides)
  const [roleOverrides, setRoleOverrides] = useState<Record<string, string>>(initial.roleOverrides)
  const [defaultTeamId, setDefaultTeamId] = useState<string | undefined>(initial.defaultTeamId)
  const [confirmAllBranches, setConfirmAllBranches] = useState(initial.confirmAllBranches)

  const branchOptions = accessibleBranches.map((branch) => ({
    value: branch.id,
    label: branch.name
  }))

  useEffect(() => {
    if (teamIds.length === 0) {
      setDefaultTeamId(undefined)
      return
    }
    if (!defaultTeamId || !teamIds.includes(defaultTeamId)) {
      setDefaultTeamId(teamIds[0])
    }
  }, [teamIds])

  useEffect(() => {
    setRoleOverrides((prev) => {
      const next: Record<string, string> = {}
      for (const id of teamIds) {
        if (prev[id]) next[id] = prev[id]
      }
      return next
    })
  }, [teamIds])

  useEffect(() => {
    if (mode === MODE_ALL) {
      onChange(
        confirmAllBranches && defaultRoleCode
          ? {
              mode: MODE_ALL,
              default_role_code: defaultRoleCode,
              confirm_all_branches: true
            }
          : undefined
      )
      return
    }
    if (teamIds.length === 0 || !defaultRoleCode) {
      onChange(undefined)
      return
    }
    onChange({
      mode: MODE_SPECIFIC,
      team_ids: teamIds,
      default_role_code: defaultRoleCode,
      role_overrides: showOverrides ? roleOverrides : undefined,
      default_team_id: teamIds.length > 1 ? defaultTeamId : undefined
    })
  }, [mode, teamIds, defaultRoleCode, roleOverrides, showOverrides, defaultTeamId, confirmAllBranches])

  const branchNameById = (id: string) => accessibleBranches.find((b) => b.id === id)?.name ?? id
  const roleLabelByCode = (code?: string) =>
    grantableRoles.find((r) => r.value === code)?.label ?? code

  const summary =
    mode === MODE_ALL
      ? defaultRoleCode
        ? `All current and future branches — ${roleLabelByCode(defaultRoleCode)}`
        : "All current and future branches — select a role"
      : teamIds.length === 0
        ? "Select at least one branch"
        : `${teamIds.map(branchNameById).join(", ")} — ${
            showOverrides
              ? teamIds
                  .map((id) => `${branchNameById(id)}: ${roleLabelByCode(roleOverrides[id] ?? defaultRoleCode)}`)
                  .join("; ")
              : (roleLabelByCode(defaultRoleCode) ?? "select a role")
          }`

  return (
    <div className="mb-4">
      <label className="ant-form-item-label">
        <Text>Branch access</Text>
      </label>

      <Radio.Group
        className="mb-3 flex"
        disabled={disabled}
        value={mode}
        onChange={(e) => setMode(e.target.value)}
        options={[
          { label: "Specific branches", value: MODE_SPECIFIC },
          { label: "All current and future branches", value: MODE_ALL }
        ]}
      />

      {mode === MODE_SPECIFIC && (
        <>
          <Select
            className="w-full mb-3"
            mode="multiple"
            showSearch
            disabled={disabled}
            placeholder="Select one or more branches"
            optionFilterProp="label"
            value={teamIds}
            onChange={setTeamIds}
            options={branchOptions}
          />

          <Select
            className="w-full mb-2"
            disabled={disabled}
            placeholder="Default role for the selected branches"
            options={grantableRoles}
            value={defaultRoleCode}
            onChange={setDefaultRoleCode}
          />

          {teamIds.length > 1 && (
            <>
              <Checkbox
                className="mb-2"
                disabled={disabled}
                checked={showOverrides}
                onChange={(e) => setShowOverrides(e.target.checked)}
              >
                Different roles by branch
              </Checkbox>

              {showOverrides && (
                <div className="mb-3 flex flex-col gap-2 pl-4">
                  {teamIds.map((id) => (
                    <div key={id} className="flex items-center gap-2">
                      <Text className="w-32 shrink-0">{branchNameById(id)}</Text>
                      <Select
                        className="w-full"
                        disabled={disabled}
                        placeholder={roleLabelByCode(defaultRoleCode) ?? "Default role"}
                        options={grantableRoles}
                        value={roleOverrides[id]}
                        allowClear
                        onChange={(value) =>
                          setRoleOverrides((prev) => ({ ...prev, [id]: value }))
                        }
                      />
                    </div>
                  ))}
                </div>
              )}

              <Select
                className="w-full mb-2"
                disabled={disabled}
                placeholder="Default branch"
                options={branchOptions.filter((o) => teamIds.includes(o.value))}
                value={defaultTeamId}
                onChange={setDefaultTeamId}
              />
            </>
          )}
        </>
      )}

      {mode === MODE_ALL && (
        <>
          <Select
            className="w-full mb-3"
            disabled={disabled}
            placeholder="Role for all current and future branches"
            options={grantableRoles}
            value={defaultRoleCode}
            onChange={setDefaultRoleCode}
          />
          <Checkbox
            className="mb-3"
            disabled={disabled}
            checked={confirmAllBranches}
            onChange={(e) => setConfirmAllBranches(e.target.checked)}
          >
            I understand this grants access to every current branch and any
            branch created in the future.
          </Checkbox>
        </>
      )}

      <Alert type="info" showIcon message={summary} />
    </div>
  )
}
